import React, { useEffect, useState } from 'react'
import Hero from '../components/nav/Hero'
import { Helmet } from 'react-helmet'
import { useLocation, Link } from 'react-router-dom'
import { FiDollarSign, FiGlobe, FiRefreshCw, FiShield, FiMapPin, FiBriefcase, FiClock, FiArrowRight, FiX, FiCheckCircle } from 'react-icons/fi'
import Empty from '../components/feedback/Empty'
import blogServices from '../services/BlogServices'
import FadeInSection from '../components/nav/FadeInSection'
import about from '../assets/about.png'

function CareersPage() {
    const url = window.location.href
    const location = useLocation()
    const [stats, setStats] = useState()
    const [vacancies, setVacancies] = useState([])
    const [loading, setLoading] = useState(true)
    const [selectedVacancy, setSelectedVacancy] = useState(null)
    const [nextPage, setNextPage] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)

    const getStats = () => {
        blogServices.getCareerStats().then(
            res => setStats(res.data?.available_vacancies),
            err => console.log(err)
        )
    }

    const fetchVacancies = (page = 1) => {
        setLoading(true)
        blogServices.getCareers(page)
            .then(res => {
                setVacancies(res.data.results || [])
                setNextPage(res.data.next)
                setCurrentPage(page)
                setLoading(false)
            })
            .catch(err => {
                console.log(err)
                setLoading(false)
            })
    }

    useEffect(() => {
        getStats()
        fetchVacancies(1)
    }, [])

    useEffect(() => {
        const hash = location.hash
        if (hash) {
            const element = document.querySelector(hash)
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' })
                }, 54)
            }
        }
    }, [location.hash])

    const getSnippet = (html, maxLength = 140) => {
        if (!html) return ''
        const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    }

    const timeAgo = (dateStr) => {
        const diff = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24))
        if (diff === 0) return 'Today'
        if (diff === 1) return '1d ago'
        if (diff < 7) return `${diff}d ago`
        if (diff < 30) return `${Math.floor(diff / 7)}w ago`
        return `${Math.floor(diff / 30)}mo ago`
    }

    const closeModal = () => setSelectedVacancy(null)

    return (
        <div>
            <Helmet>
                <title>Security Careers & Job Opportunities | Guardhart Kenya</title>
                <link rel="canonical" href={url} />
                <meta name="description" content="Build a career in elite security with Guardhart. PSRA licensed security jobs in Kenya with fair wages, rigorous training, and global standards. Join 100+ professional guards across Nairobi, Kisumu, Mombasa, and 6 branches nationwide." />

                <meta property="og:type" content="website" />
                <meta property="og:url" content={url} />
                <meta property="og:title" content="Security Careers & Job Opportunities | Guardhart Kenya" />
                <meta property="og:description" content="Build a career in elite security with Guardhart. PSRA licensed security jobs in Kenya with fair wages, rigorous training, and global standards. Join 100+ professional guards across 6 branches nationwide." />

                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:title" content="Security Careers & Job Opportunities | Guardhart Kenya" />
                <meta property="twitter:description" content="Build a career in elite security with Guardhart. PSRA licensed security jobs in Kenya with fair wages, rigorous training, and global standards." />
            </Helmet>

            <Hero
                label="Role Vacancies"
                title="Join our Team"
                description="Build a career in elite security with a company that values expertise, integrity, and fair compensation. We are defining the future of protection in East Africa."
                primaryBtnText="Explore Openings"
                primaryBtnLink="#vacancies"
                showSecondaryBadge={false}
            />

            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Benefits Section */}
                <FadeInSection>
                    <section className="py-16 border-b border-zinc-200">
                        <div className="grid grid-cols-1 md:grid-cols-3 border border-zinc-200">
                            <div className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-zinc-200 group hover:border-primary/30 transition-colors hover:bg-primary/5 duration-300">
                                <div className="mb-8">
                                    <FiDollarSign className="text-primary text-4xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-base-content mb-4">Fair Wages</h3>
                                <p className="text-sm text-zinc-600 leading-relaxed">
                                    Industry-leading compensation structure for all available roles, with comprehensive medical cover and benefits.
                                </p>
                            </div>
                            <div className="p-8 md:p-12 border-b md:border-b-0 md:border-r border-zinc-200 group hover:border-primary/30 transition-colors hover:bg-primary/5 duration-300">
                                <div className="mb-8">
                                    <FiRefreshCw className="text-primary text-4xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-base-content mb-4">Rigorous Training</h3>
                                <p className="text-sm text-zinc-600 leading-relaxed">
                                    5,000+ training hours delivered annually through our in-house academy, covering tactical response, surveillance, and conflict de-escalation.
                                </p>
                            </div>
                            <div className="p-8 md:p-12 group hover:border-primary/30 transition-colors hover:bg-primary/5 duration-300">
                                <div className="mb-8">
                                    <FiGlobe className="text-primary text-4xl" />
                                </div>
                                <h3 className="text-2xl font-bold text-base-content mb-4">Global Standards</h3>
                                <p className="text-sm text-zinc-600 leading-relaxed">
                                    We operate under ISO 18788 international security protocols, giving you experience respected by elite agencies worldwide.
                                </p>
                            </div>
                        </div>
                    </section>
                </FadeInSection>

                <FadeInSection>
                    <section className="py-16">
                        <div className="flex flex-col lg:flex-row gap-12 items-center">
                            <div className="w-full lg:w-1/2">
                                <div className="relative aspect-video overflow-hidden rounded-lg border border-zinc-200">
                                    <img
                                        className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
                                        alt="Guardhart security team in formation"
                                        src={about}
                                    />
                                </div>
                            </div>
                            <div className="w-full lg:w-1/2">
                                <span className="text-xs font-bold uppercase tracking-widest text-primary mb-4 block">Our Philosophy</span>
                                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-base-content mb-6">The Guardhart Way</h2>
                                <p className="text-base text-zinc-600 mb-6 leading-relaxed">
                                    At Guardhart, we don't just hire guards; we recruit professionals. Our culture is built on the pillars of technical precision, unwavering integrity, and mutual respect. Every member of our team is an ambassador of safety.
                                </p>
                                <p className="text-base text-zinc-600 leading-relaxed">
                                    We maintain a flat hierarchy where performance and character are the only metrics for advancement. If you thrive in disciplined environments and value excellence, you belong here.
                                </p>
                            </div>
                        </div>
                    </section>
                </FadeInSection>

                <FadeInSection>
                    <section className="py-16" id="vacancies">
                        <div className="flex justify-between items-end mb-12">
                            <div>
                                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-base-content mb-2">Current Openings</h2>
                                <p className="text-zinc-600">{stats || 0} positions active as of today</p>
                            </div>
                            <div className="hidden md:block h-px flex-1 bg-zinc-300 mx-8 mb-4"></div>
                        </div>

                        {loading && vacancies.length === 0 ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className="animate-pulse bg-base-100 border border-zinc-200 rounded-lg p-6">
                                        <div className="flex items-start gap-6">
                                            <div className="bg-zinc-200 w-12 h-12 rounded"></div>
                                            <div className="flex-1">
                                                <div className="bg-zinc-200 h-6 w-1/2 rounded mb-3"></div>
                                                <div className="bg-zinc-200 h-4 w-1/3 rounded"></div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : vacancies.length === 0 ? (
                            <Empty objectName={"Vacancies"} />
                        ) : (
                            <div className="space-y-4">
                                {vacancies.map(vacancy => (
                                    <div
                                        key={vacancy.id}
                                        className="group flex flex-col md:flex-row md:items-center justify-between gap-6 p-6 border border-zinc-200 bg-base-100 rounded-lg hover:border-primary/40 hover:shadow-lg hover:shadow-zinc-200/50 hover:-translate-y-0.5 transition-all duration-300"
                                    >
                                        <div className="flex items-start gap-6 flex-1 min-w-0">
                                            <div className="w-12 h-12 flex items-center justify-center bg-base-200 border border-zinc-200 rounded-lg group-hover:border-primary/30 group-hover:bg-primary/5 transition-colors shrink-0">
                                                <FiShield className="text-zinc-500 group-hover:text-primary w-5 h-5 transition-colors" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-xl font-bold text-base-content mb-2 group-hover:text-primary transition-colors">
                                                    {vacancy.title}
                                                </h4>
                                                <p className="text-sm text-zinc-600 leading-relaxed line-clamp-2 mb-3">
                                                    {getSnippet(vacancy.description)}
                                                </p>
                                                <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                                                    <span className="flex items-center gap-1.5">
                                                        <FiMapPin className="w-3.5 h-3.5 text-primary" />
                                                        {vacancy.location}
                                                    </span>
                                                    <span className="flex items-center gap-1.5">
                                                        <FiBriefcase className="w-3.5 h-3.5 text-primary" />
                                                        {vacancy.job_type}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 shrink-0">
                                            <span className="hidden md:block text-[10px] text-zinc-500 uppercase tracking-widest font-bold">
                                                {timeAgo(vacancy.created_at)}
                                            </span>
                                            <button
                                                onClick={() => setSelectedVacancy(vacancy)}
                                                className="px-6 py-2.5 border border-zinc-300 text-base-content bg-base-100 group-hover:bg-primary group-hover:text-primary-content group-hover:border-primary font-bold text-[11px] uppercase tracking-widest transition-all rounded"
                                            >
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {nextPage && (
                            <div className="mt-12 flex justify-center">
                                <button
                                    onClick={() => fetchVacancies(currentPage + 1)}
                                    className="inline-flex items-center gap-2 border border-zinc-300 text-base-content px-6 py-3 text-xs font-bold uppercase tracking-widest rounded hover:bg-base-200 transition-all"
                                >
                                    Load More Openings
                                    <FiArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </section>
                </FadeInSection>
            </div>

            {selectedVacancy && (
                <>
                    <input
                        type="checkbox"
                        id="vacancy-modal"
                        className="modal-toggle"
                        checked={true}
                        readOnly
                        onChange={closeModal}
                    />
                    <label htmlFor="vacancy-modal" className="modal cursor-pointer backdrop-blur-sm">
                        <label
                            className="modal-box bg-base-100 border border-zinc-200 max-w-3xl p-0 overflow-hidden"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="bg-base-200 p-8 border-b border-zinc-200 relative">
                                <button
                                    onClick={closeModal}
                                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-base-100 border border-zinc-200 text-zinc-500 hover:text-base-content hover:border-primary/40 transition-all"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                                <div className="flex items-start gap-4 pr-10">
                                    <div className="w-14 h-14 flex items-center justify-center bg-primary/10 border border-primary/20 rounded-lg shrink-0">
                                        <FiShield className="text-primary w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl md:text-3xl font-bold text-base-content mb-3 leading-tight">
                                            {selectedVacancy.title}
                                        </h3>
                                        <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600">
                                            <span className="flex items-center gap-1.5">
                                                <FiMapPin className="w-4 h-4 text-primary" />
                                                {selectedVacancy.location}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <FiBriefcase className="w-4 h-4 text-primary" />
                                                {selectedVacancy.job_type}
                                            </span>
                                            <span className="flex items-center gap-1.5">
                                                <FiClock className="w-4 h-4 text-primary" />
                                                Posted {timeAgo(selectedVacancy.created_at)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="p-8 max-h-[60vh] overflow-y-auto">
                                <div className="mb-8">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Role Overview</h4>
                                    <div
                                        className="prose prose-zinc max-w-none prose-p:text-zinc-600 prose-p:leading-relaxed prose-p:mb-4 prose-li:text-zinc-600 prose-strong:text-base-content prose-a:text-primary"
                                        dangerouslySetInnerHTML={{ __html: selectedVacancy.description }}
                                    />
                                </div>

                                <div className="mb-8">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Requirements</h4>
                                    <div
                                        className="prose prose-zinc max-w-none prose-p:text-zinc-600 prose-p:leading-relaxed prose-li:text-zinc-600 prose-li:marker:text-primary prose-strong:text-base-content prose-a:text-primary"
                                        dangerouslySetInnerHTML={{ __html: selectedVacancy.requirements }}
                                    />
                                </div>

                                <div className="bg-base-200 border border-zinc-200 rounded-lg p-6">
                                    <h4 className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Why Guardhart</h4>
                                    <ul className="space-y-2 text-sm text-zinc-600">
                                        <li className="flex items-start gap-2"><FiCheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Fair compensation/wages</li>
                                        <li className="flex items-start gap-2"><FiCheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> WIBA insurance</li>
                                        <li className="flex items-start gap-2"><FiCheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> 5,000+ hours of continuous training per year</li>
                                        <li className="flex items-start gap-2"><FiCheckCircle className="w-4 h-4 text-primary mt-0.5 shrink-0" /> Clear career progression to supervisor and beyond</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="p-8 border-t border-zinc-200 bg-base-200 flex flex-col sm:flex-row gap-3">
                                {/* <a
                                    href={`mailto:careers@guardhart.co.ke?subject=Application: ${encodeURIComponent(selectedVacancy.title)}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-primary text-primary-content px-6 py-3 text-xs font-bold uppercase tracking-widest rounded hover:brightness-110 active:scale-95 transition-all"
                                >
                                    Apply Now
                                    <FiArrowRight className="w-4 h-4" />
                                </a> */}
                                <button
                                    onClick={closeModal}
                                    className="flex-1 inline-flex items-center justify-center gap-2 border border-zinc-300 text-base-content px-6 py-3 text-xs font-bold uppercase tracking-widest rounded hover:bg-base-100 transition-all"
                                >
                                    Close
                                </button>
                            </div>
                        </label>
                    </label>
                </>
            )}
        </div>
    )
}

export default CareersPage